import { useCallback, useEffect, useMemo, useState } from "react";
import {
	X,
	ChevronDown,
	Check,
	Clock,
	ShieldCheck,
	MapPin,
	Plus,
	Loader2,
	Tag,
} from "lucide-react";
import {
	normalizeTime12Input,
	parseTime12,
} from "../../../core/utils/provider-registration";
import {
	searchAdministrativeNominatim,
	type AdministrativeSuggestion,
} from "../../user-details/nominatim";
import type {
	OrderListService,
	OrderListModalProps,
} from "../../../core/types/tsx/components/modals/clientmodals/orderlist.types";

export default OrderListModal;
export type {
	OrderListService,
	OrderListModalProps,
	OrderListSubmitPayload,
	AddressSummary,
	NewAddressPayload,
	ServiceContextForm,
} from "../../../core/types/tsx/components/modals/clientmodals/orderlist.types";

const formatPrice = (value: number) => {
	if (Number.isNaN(value)) return "$0.00";
	return `$${value.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const buildAutoRequestDescription = (selectedItems: OrderListService[]) => {
	const serviceNames = selectedItems
		.map((item) => item.name?.trim())
		.filter((name): name is string => Boolean(name));

	if (serviceNames.length === 0) return "servicio";
	return `servicio ${serviceNames.join(", ")}`;
};

const formatTimeByRegex = (rawValue: string): string => {
	const digits = rawValue.replace(/\D/g, "").slice(0, 4);
	if (!digits) return "";

	if (digits.length === 1) {
		return digits;
	}

	if (digits.length === 2) {
		const hour = Number(digits);
		if (!Number.isFinite(hour) || hour < 1 || hour > 12) {
			return digits[0] ?? "";
		}
		return digits;
	}

	const hourPart = digits.slice(0, 2);
	const hour = Number(hourPart);
	if (!Number.isFinite(hour) || hour < 1 || hour > 12) {
		return digits[0] ?? "";
	}

	if (digits.length === 3) {
		return `${hourPart}:${digits.slice(2)}`;
	}

	const minutePart = digits.slice(2, 4);
	const minute = Number(minutePart);
	if (!Number.isFinite(minute) || minute > 59) {
		return `${hourPart}:${minutePart[0] ?? ""}`;
	}

	return `${hourPart}:${minutePart}`;
};

export function OrderListModal({
	open,
	onClose,
	services,
	provider,
	onChangeQuantity,
	onSubmit,
	title = "Seleccionar Servicios del Proveedor",
	submitLabel = "Solicitar Pedido",
	addresses = [],
	loadingAddresses = false,
	onCreateAddress,
	requireServiceContext = false,
	serviceContext,
	onServiceContextChange,
}: OrderListModalProps) {
	const [items, setItems] = useState<OrderListService[]>([]);
	const [expandedId, setExpandedId] = useState<OrderListService["id"] | null>(null);
	const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
	const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
	const [showAddressForm, setShowAddressForm] = useState(false);
	const [addressForm, setAddressForm] = useState({
		name_state: "",
		name_city: "",
		name_address: "",
		street_address: "",
		reference_address: "",
	});
	const [addressErrors, setAddressErrors] = useState<{
		name_state?: string;
		name_city?: string;
		name_address?: string;
		street_address?: string;
		reference_address?: string;
	}>({});
	const [stateSuggestions, setStateSuggestions] = useState<AdministrativeSuggestion[]>(
		[],
	);
	const [citySuggestions, setCitySuggestions] = useState<AdministrativeSuggestion[]>(
		[],
	);
	const [stateSuggestStatus, setStateSuggestStatus] = useState<
		"idle" | "loading" | "error"
	>("idle");
	const [citySuggestStatus, setCitySuggestStatus] = useState<
		"idle" | "loading" | "error"
	>("idle");
	const [stateSuggestAbort, setStateSuggestAbort] = useState<AbortController | null>(
		null,
	);
	const [citySuggestAbort, setCitySuggestAbort] = useState<AbortController | null>(
		null,
	);
	const [stateSuggestDebounce, setStateSuggestDebounce] = useState<number | null>(null);
	const [citySuggestDebounce, setCitySuggestDebounce] = useState<number | null>(null);
	const [requestDescription, setRequestDescription] = useState("");
	const [savingAddress, setSavingAddress] = useState(false);

	const now = new Date();
	const currentYear = now.getFullYear();
	const isDecember = now.getMonth() === 11;
	const maxAllowedYear = isDecember ? currentYear + 1 : currentYear;
	const todayIso = new Date(now.getFullYear(), now.getMonth(), now.getDate())
		.toISOString()
		.slice(0, 10);
	const maxDateIso = `${maxAllowedYear}-12-31`;

	// Unique categories derived from available services
	const availableCategories = useMemo(
		() =>
			Array.from(
				new Set(
					items.map((i) => i.category).filter((c): c is string => Boolean(c)),
				),
			),
		[items],
	);

	// Only show services that belong to the selected category
	const displayedItems = useMemo(
		() =>
			selectedCategory ? items.filter((i) => i.category === selectedCategory) : [],
		[items, selectedCategory],
	);

	const isValidAvailabilityDate = useCallback(
		(value: string) => {
			if (!value) return true;

			const selected = new Date(`${value}T00:00:00`);
			if (Number.isNaN(selected.getTime())) return false;

			const selectedYear = selected.getFullYear();
			if (selectedYear < currentYear || selectedYear > maxAllowedYear) {
				return false;
			}

			const selectedIso = new Date(
				selected.getFullYear(),
				selected.getMonth(),
				selected.getDate(),
			)
				.toISOString()
				.slice(0, 10);

			return selectedIso >= todayIso && selectedIso <= maxDateIso;
		},
		[currentYear, maxAllowedYear, todayIso, maxDateIso],
	);

	useEffect(() => {
		setItems(
			services.map((service) => ({ ...service, quantity: service.quantity ?? 0 })),
		);
	}, [services]);

	// Auto-select category: pick the one from serviceContext, or auto-pick when only 1 exists
	useEffect(() => {
		if (availableCategories.length === 0) return;
		if (selectedCategory !== null) return; // Solo sincroniza si no hay selección manual
		if (availableCategories.length === 1) {
			setSelectedCategory(availableCategories[0]);
			return;
		}
		const contextCat = serviceContext?.category?.trim();
		if (contextCat && availableCategories.includes(contextCat)) {
			setSelectedCategory(contextCat);
		}
	}, [availableCategories, serviceContext?.category, selectedCategory]);

	// Auto-select first address when addresses load
	useEffect(() => {
		if (addresses.length > 0 && selectedAddressId === null) {
			setSelectedAddressId(addresses[0].id);
		}
	}, [addresses, selectedAddressId]);

	// Reset form state when modal closes/opens
	useEffect(() => {
		if (!open) {
			stateSuggestAbort?.abort();
			citySuggestAbort?.abort();
			if (stateSuggestDebounce !== null) {
				window.clearTimeout(stateSuggestDebounce);
			}
			if (citySuggestDebounce !== null) {
				window.clearTimeout(citySuggestDebounce);
			}
			setItems(
				services.map((service) => ({
					...service,
					quantity: 0,
				})),
			);
			setExpandedId(null);
			setShowAddressForm(false);
			setAddressForm({
				name_state: "",
				name_city: "",
				name_address: "",
				street_address: "",
				reference_address: "",
			});
			setAddressErrors({});
			setStateSuggestions([]);
			setCitySuggestions([]);
			setStateSuggestStatus("idle");
			setCitySuggestStatus("idle");
			setStateSuggestAbort(null);
			setCitySuggestAbort(null);
			setStateSuggestDebounce(null);
			setCitySuggestDebounce(null);
			setSelectedAddressId(null);
			setSelectedCategory(null);
			setRequestDescription("");
		}
	}, [
		open,
		services,
		stateSuggestAbort,
		citySuggestAbort,
		stateSuggestDebounce,
		citySuggestDebounce,
	]);

	const handleStateInput = (nextValue: string) => {
		setAddressForm((prev) => ({
			...prev,
			name_state: nextValue,
		}));
		setAddressErrors((prev) => ({ ...prev, name_state: undefined }));

		stateSuggestAbort?.abort();
		if (stateSuggestDebounce !== null) {
			window.clearTimeout(stateSuggestDebounce);
		}

		setStateSuggestStatus("idle");
		const query = nextValue.trim();
		if (query.length < 2) {
			setStateSuggestions([]);
			setStateSuggestAbort(null);
			setStateSuggestDebounce(null);
			return;
		}

		const timer = window.setTimeout(async () => {
			setStateSuggestStatus("loading");
			const controller = new AbortController();
			setStateSuggestAbort(controller);
			try {
				const results = await searchAdministrativeNominatim(query, {
					level: "state",
					country: "Venezuela",
					limit: 8,
					signal: controller.signal,
				});
				setStateSuggestions(results);
				setStateSuggestStatus("idle");
			} catch {
				setStateSuggestStatus("error");
			}
		}, 300);

		setStateSuggestDebounce(timer);
	};

	const handleCityInput = (nextValue: string) => {
		setAddressForm((prev) => ({
			...prev,
			name_city: nextValue,
		}));
		setAddressErrors((prev) => ({ ...prev, name_city: undefined }));

		citySuggestAbort?.abort();
		if (citySuggestDebounce !== null) {
			window.clearTimeout(citySuggestDebounce);
		}

		setCitySuggestStatus("idle");
		const query = nextValue.trim();
		if (query.length < 2) {
			setCitySuggestions([]);
			setCitySuggestAbort(null);
			setCitySuggestDebounce(null);
			return;
		}

		const timer = window.setTimeout(async () => {
			setCitySuggestStatus("loading");
			const controller = new AbortController();
			setCitySuggestAbort(controller);
			try {
				const results = await searchAdministrativeNominatim(query, {
					level: "city",
					stateContext: addressForm.name_state,
					country: "Venezuela",
					limit: 8,
					signal: controller.signal,
				});
				setCitySuggestions(results);
				setCitySuggestStatus("idle");
			} catch {
				setCitySuggestStatus("error");
			}
		}, 300);

		setCitySuggestDebounce(timer);
	};

	const selectStateSuggestion = (suggestion: AdministrativeSuggestion) => {
		stateSuggestAbort?.abort();
		if (stateSuggestDebounce !== null) {
			window.clearTimeout(stateSuggestDebounce);
		}
		setAddressForm((prev) => ({
			...prev,
			name_state: suggestion.value,
			name_address:
				prev.name_address.trim() || prev.name_city.trim()
					? `${prev.name_city.trim()}, ${suggestion.value}`
					: prev.name_address,
		}));
		setAddressErrors((prev) => ({ ...prev, name_state: undefined }));
		setStateSuggestions([]);
		setStateSuggestStatus("idle");
		setStateSuggestAbort(null);
		setStateSuggestDebounce(null);
	};

	const selectCitySuggestion = (suggestion: AdministrativeSuggestion) => {
		citySuggestAbort?.abort();
		if (citySuggestDebounce !== null) {
			window.clearTimeout(citySuggestDebounce);
		}
		setAddressForm((prev) => ({
			...prev,
			name_city: suggestion.value,
			name_address:
				prev.name_address.trim() || prev.name_state.trim()
					? `${suggestion.value}, ${prev.name_state.trim()}`
					: prev.name_address,
		}));
		setAddressErrors((prev) => ({ ...prev, name_city: undefined }));
		setCitySuggestions([]);
		setCitySuggestStatus("idle");
		setCitySuggestAbort(null);
		setCitySuggestDebounce(null);
	};

	useEffect(() => {
		if (!requireServiceContext || !serviceContext || !onServiceContextChange) return;

		const selectedServices = items.filter((item) => (item.quantity ?? 0) > 0);
		// Use the explicitly chosen category from the picker; fall back to deriving from selected services
		const derivedCategory =
			(selectedCategory?.trim() ||
				selectedServices
					.map((service) => service.category?.trim())
					.find((category): category is string => Boolean(category))) ??
			"";

		const derivedSubcategory = Array.from(
			new Set(
				selectedServices
					.map((service) => service.subcategory?.trim() || service.name.trim())
					.filter(Boolean),
			),
		);

		const selectedAddress = addresses.find(
			(address) => address.id === selectedAddressId,
		);
		const nextAddressName = selectedAddress?.name_address ?? "";
		const nextReferences = selectedAddress?.reference_address ?? "";

		const isSameSubcategory =
			serviceContext.subcategory.length === derivedSubcategory.length &&
			serviceContext.subcategory.every(
				(subcategory, index) => subcategory === derivedSubcategory[index],
			);

		if (
			serviceContext.category === derivedCategory &&
			isSameSubcategory &&
			serviceContext.addressName === nextAddressName &&
			serviceContext.references === nextReferences
		) {
			return;
		}

		onServiceContextChange({
			category: derivedCategory,
			subcategory: derivedSubcategory,
			addressName: nextAddressName,
			references: nextReferences,
		});
	}, [
		requireServiceContext,
		serviceContext,
		serviceContext?.category,
		serviceContext?.subcategory,
		serviceContext?.addressName,
		serviceContext?.references,
		onServiceContextChange,
		items,
		addresses,
		selectedAddressId,
		selectedCategory,
	]);

	useEffect(() => {
		if (!requireServiceContext || !serviceContext || !onServiceContextChange) return;
		if (!serviceContext.date) return;
		if (isValidAvailabilityDate(serviceContext.date)) return;

		onServiceContextChange({ date: "" });
	}, [
		requireServiceContext,
		serviceContext,
		onServiceContextChange,
		isValidAvailabilityDate,
		todayIso,
		maxDateIso,
	]);

	const handleQtyChange = (id: OrderListService["id"], delta: number) => {
		setItems((prev) =>
			prev.map((item) => {
				if (item.id !== id) return item;
				const nextQty = Math.max(0, (item.quantity ?? 0) + delta);
				onChangeQuantity?.(id, nextQty);
				return { ...item, quantity: nextQty };
			}),
		);
	};

	const handleCategorySelect = (cat: string) => {
		if (cat === selectedCategory) return;
		// Reset all quantities when switching category to avoid cross-category orders
		setItems((prev) => prev.map((item) => ({ ...item, quantity: 0 })));
		setSelectedCategory(cat);
		setExpandedId(null);
	};

	const toggleExpand = (id: OrderListService["id"]) => {
		setExpandedId((prev) => (prev === id ? null : id));
	};

	const handleSaveAddress = async () => {
		const normalizedState = addressForm.name_state.trim();
		const normalizedCity = addressForm.name_city.trim();
		const normalizedNameAddress = addressForm.name_address.trim();
		const normalizedStreet = addressForm.street_address.trim();
		const normalizedReference = addressForm.reference_address.trim();

		const nextErrors: {
			name_state?: string;
			name_city?: string;
			name_address?: string;
			street_address?: string;
			reference_address?: string;
		} = {};
		if (!normalizedState) nextErrors.name_state = "El estado es obligatorio";
		if (!normalizedCity) nextErrors.name_city = "La ciudad es obligatoria";
		if (!normalizedNameAddress) nextErrors.name_address = "El nombre es obligatorio";
		if (!normalizedStreet) nextErrors.street_address = "La calle es obligatoria";
		if (!normalizedReference)
			nextErrors.reference_address = "La referencia es obligatoria";

		if (Object.keys(nextErrors).length > 0) {
			setAddressErrors(nextErrors);
			return;
		}
		setSavingAddress(true);
		try {
			await onCreateAddress?.({
				name_state: normalizedState,
				name_city: normalizedCity,
				name_address: normalizedNameAddress,
				street_address: normalizedStreet,
				reference_address: normalizedReference,
			});
			setAddressForm({
				name_state: "",
				name_city: "",
				name_address: "",
				street_address: "",
				reference_address: "",
			});
			setAddressErrors({});
			setStateSuggestions([]);
			setCitySuggestions([]);
			setStateSuggestStatus("idle");
			setCitySuggestStatus("idle");
			setShowAddressForm(false);
		} finally {
			setSavingAddress(false);
		}
	};

	const total = items.reduce((sum, item) => sum + item.price * (item.quantity ?? 0), 0);
	const selectedItemsForDescription = items.filter((item) => (item.quantity ?? 0) > 0);
	const autoRequestDescription = buildAutoRequestDescription(
		selectedItemsForDescription,
	);
	// Validación de hora: mínimo 2 horas después de la actual si es hoy
	const isTimeAtLeastTwoHoursLater = () => {
		if (!requireServiceContext || !serviceContext) return true;
		if (!serviceContext.date || !serviceContext.time || !serviceContext.period)
			return true;
		const now = new Date();
		// Fecha seleccionada en zona local
		const [year, month, day] = serviceContext.date.split("-").map(Number);
		if (!year || !month || !day) return true;
		const selectedDate = new Date(year, month - 1, day);
		// Si la fecha no es hoy, no validar
		const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		if (
			selectedDate.getFullYear() !== today.getFullYear() ||
			selectedDate.getMonth() !== today.getMonth() ||
			selectedDate.getDate() !== today.getDate()
		) {
			return true;
		}

		// Convertir hora seleccionada a 24h
		const [h, m] = serviceContext.time.split(":");
		let hour = Number(h);
		const minute = Number(m);
		if (serviceContext.period === "PM" && hour < 12) hour += 12;
		if (serviceContext.period === "AM" && hour === 12) hour = 0;
		const selected = new Date(year, month - 1, day, hour, minute, 0, 0);

		// Ahora + 2 horas
		const minAllowed = new Date(now.getTime() + 2 * 60 * 60 * 1000);
		return selected.getTime() >= minAllowed.getTime();
	};

	const hasInvalidServiceContextTime =
		requireServiceContext && serviceContext
			? (Boolean(serviceContext.time.trim()) &&
					!parseTime12(serviceContext.time)) ||
				!isTimeAtLeastTwoHoursLater()
			: false;

	if (!open) return null;

	return (
		<div className='fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-4'>
			<div className='relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl'>
				<header className='flex items-center justify-between border-b border-slate-200 px-6 py-4'>
					<h2 className='text-base font-semibold text-slate-900'>{title}</h2>
					<button
						type='button'
						onClick={onClose}
						aria-label='Cerrar'
						className='rounded-full p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 cursor-pointer'
					>
						<X className='h-4 w-4' />
					</button>
				</header>

				<div className='flex-1 overflow-y-auto'>
					{provider && (
						<div className='px-6 pt-4'>
							<div className='flex items-center gap-4 rounded-xl border border-slate-200 px-4 py-3'>
								{provider.avatarUrl ? (
									<img
										src={provider.avatarUrl}
										alt={provider.name}
										className='h-11 w-11 rounded-xl object-cover'
									/>
								) : (
									<div className='h-11 w-11 rounded-xl bg-slate-200' />
								)}
								<div className='min-w-0 flex-1'>
									<div className='flex flex-wrap items-center justify-between gap-2'>
										<div className='min-w-0'>
											<div className='truncate text-sm font-semibold text-slate-900'>
												{provider.name}
											</div>
											<div className='mt-0.5 truncate text-xs text-slate-500'>
												{provider.roleLabel ?? "Proveedor"}
											</div>
										</div>

										<div className='flex items-center gap-2'>
											{provider.status ? (
												<span className='rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700'>
													{provider.status}
												</span>
											) : null}
											{provider.priceRange ? (
												<span className='text-xs font-semibold text-primary'>
													{provider.priceRange}
												</span>
											) : null}
										</div>
									</div>
								</div>
							</div>
						</div>
					)}

					{/* ── Category picker ── */}
					<div className='px-6 pt-4'>
						<h3 className='mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-900'>
							<Tag className='h-4 w-4 text-primary' />
							Categoría de servicio
						</h3>
						{availableCategories.length === 0 ? (
							<p className='text-xs text-slate-400'>Cargando servicios…</p>
						) : (
							<div className='flex flex-wrap gap-2'>
								{availableCategories.map((cat) => (
									<button
										key={cat}
										type='button'
										onClick={() => handleCategorySelect(cat)}
										className={`rounded-full border px-3 py-1 text-xs font-medium transition cursor-pointer ${
											selectedCategory === cat
												? "border-primary bg-primary text-white"
												: "border-slate-300 bg-white text-slate-600 hover:border-primary hover:text-primary"
										}`}
									>
										{cat}
									</button>
								))}
							</div>
						)}
						{availableCategories.length > 1 && !selectedCategory && (
							<p className='mt-2 text-[11px] text-amber-600'>
								Selecciona una categoría para ver los servicios
								disponibles.
							</p>
						)}
					</div>

					{selectedCategory && (
						<div className='px-6 pt-3'>
							<h3 className='text-sm font-semibold text-slate-900'>
								Servicios disponibles ({displayedItems.length})
							</h3>
						</div>
					)}

					<div className='px-6 py-4'>
						<div className='space-y-2'>
							{displayedItems.map((service) => {
								const isExpanded = expandedId === service.id;
								const hasDetails =
									(service.description && service.description.trim()) ||
									(service.includes && service.includes.length > 0) ||
									(service.excludes && service.excludes.length > 0) ||
									service.warrantyDays != null ||
									service.estimatedDuration != null;

								return (
									<div
										key={service.id}
										className='rounded-xl border border-slate-200 bg-white transition-shadow hover:shadow-sm'
									>
										{/* Header row */}
										<div className='flex items-center gap-3 px-4 py-3'>
											{hasDetails ? (
												<button
													type='button'
													onClick={() =>
														toggleExpand(service.id)
													}
													className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 cursor-pointer'
													aria-label={
														isExpanded
															? "Contraer detalles"
															: "Ver detalles"
													}
												>
													<ChevronDown
														className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
													/>
												</button>
											) : (
												<div className='h-6 w-6 shrink-0' />
											)}

											<div
												className={`min-w-0 flex-1 ${hasDetails ? "cursor-pointer" : ""}`}
												onClick={() =>
													hasDetails && toggleExpand(service.id)
												}
											>
												<p className='text-sm text-slate-700'>
													{service.name}
												</p>
												<p className='text-xs font-semibold text-primary'>
													{formatPrice(service.price)}
												</p>
											</div>

											<div className='flex items-center gap-2 rounded-full border border-slate-200 px-2 py-1'>
												<button
													type='button'
													onClick={() =>
														handleQtyChange(service.id, -1)
													}
													className='h-6 w-6 rounded-full text-slate-500 transition hover:bg-slate-100 cursor-pointer'
													aria-label='Disminuir'
												>
													-
												</button>
												<span className='min-w-4.5 text-center text-xs text-slate-700'>
													{service.quantity ?? 0}
												</span>
												<button
													type='button'
													onClick={() =>
														handleQtyChange(service.id, 1)
													}
													className='h-6 w-6 rounded-full text-slate-500 transition hover:bg-slate-100 cursor-pointer'
													aria-label='Aumentar'
												>
													+
												</button>
											</div>
										</div>

										{/* Expandable details */}
										{isExpanded && hasDetails && (
											<div className='border-t border-slate-100 px-4 pb-4 pt-3'>
												<div className='space-y-3'>
													{service.description &&
														service.description.trim() && (
															<div>
																<p className='text-xs font-semibold text-slate-900 tracking-wide mb-1'>
																	Descripción
																</p>
																<p className='text-xs text-slate-600 leading-relaxed'>
																	{service.description}
																</p>
															</div>
														)}

													{service.includes &&
														service.includes.length > 0 && (
															<div>
																<p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1'>
																	Incluye
																</p>
																<ul className='space-y-1'>
																	{service.includes.map(
																		(item) => (
																			<li
																				key={item}
																				className='flex items-center gap-2 text-xs text-slate-600'
																			>
																				<Check className='h-3 w-3 shrink-0 text-emerald-500' />
																				<span>
																					{item}
																				</span>
																			</li>
																		),
																	)}
																</ul>
															</div>
														)}

													{service.excludes &&
														service.excludes.length > 0 && (
															<div>
																<p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1'>
																	No Incluye
																</p>
																<ul className='space-y-1'>
																	{service.excludes.map(
																		(item) => (
																			<li
																				key={item}
																				className='flex items-center gap-2 text-xs text-slate-600'
																			>
																				<X className='h-3 w-3 shrink-0 text-rose-400' />
																				<span>
																					{item}
																				</span>
																			</li>
																		),
																	)}
																</ul>
															</div>
														)}

													<div className='flex flex-wrap gap-3'>
														{service.warrantyDays != null && (
															<div className='flex items-center gap-1.5 rounded-lg bg-slate-50 px-2.5 py-1.5'>
																<ShieldCheck className='h-3.5 w-3.5 text-primary' />
																<span className='text-xs text-slate-600'>
																	Garantía:{" "}
																	<span className='font-semibold'>
																		{
																			service.warrantyDays
																		}{" "}
																		días
																	</span>
																</span>
															</div>
														)}
														{service.estimatedDuration !=
															null && (
															<div className='flex items-center gap-1.5 rounded-lg bg-slate-50 px-2.5 py-1.5'>
																<Clock className='h-3.5 w-3.5 text-primary' />
																<span className='text-xs text-slate-600'>
																	Duración:{" "}
																	<span className='font-semibold'>
																		{
																			service.estimatedDuration
																		}
																		h
																	</span>
																</span>
															</div>
														)}
													</div>
												</div>
											</div>
										)}
									</div>
								);
							})}
						</div>
					</div>

					{/* ── Dirección de servicio ── */}
					<div className='border-t border-slate-200 px-6 py-4'>
						<div className='flex items-center justify-between mb-2'>
							<h3 className='flex items-center gap-1.5 text-sm font-semibold text-slate-900'>
								<MapPin className='h-4 w-4 text-primary' />
								Dirección de servicio
							</h3>
							{!showAddressForm && onCreateAddress && (
								<button
									type='button'
									onClick={() => setShowAddressForm(true)}
									className='flex items-center gap-1 text-xs font-medium text-primary transition hover:text-primary-hover cursor-pointer'
								>
									<Plus className='h-3.5 w-3.5' />
									Agregar
								</button>
							)}
						</div>

						{loadingAddresses ? (
							<div className='flex items-center gap-2 py-3 text-xs text-slate-400'>
								<Loader2 className='h-3.5 w-3.5 animate-spin' />
								Cargando direcciones…
							</div>
						) : addresses.length === 0 && !showAddressForm ? (
							<p className='text-xs text-slate-400 py-1'>
								No tienes direcciones registradas.{" "}
								{onCreateAddress && (
									<button
										type='button'
										onClick={() => setShowAddressForm(true)}
										className='text-primary font-medium hover:underline cursor-pointer'
									>
										Agregar una
									</button>
								)}
							</p>
						) : (
							<div className='space-y-1.5 max-h-28 overflow-y-auto'>
								{addresses.map((addr) => (
									<label
										key={addr.id}
										className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 cursor-pointer transition ${
											selectedAddressId === addr.id
												? "border-primary bg-primary/5"
												: "border-slate-200 hover:border-slate-300"
										}`}
									>
										<input
											type='radio'
											name='address'
											checked={selectedAddressId === addr.id}
											onChange={() => setSelectedAddressId(addr.id)}
											className='mt-0.5 accent-primary'
										/>
										<div className='min-w-0 flex-1'>
											<p className='text-xs font-semibold text-slate-800 truncate'>
												{addr.name_address}
											</p>
											<p className='text-[11px] text-slate-500 truncate'>
												{addr.street_address}
											</p>
										</div>
									</label>
								))}
							</div>
						)}

						{/* Inline form to create a new address */}
						{showAddressForm && (
							<div className='mt-2 space-y-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3'>
								<div>
									<input
										type='text'
										placeholder='Estado (ej: Miranda)'
										value={addressForm.name_state}
										onChange={(e) => handleStateInput(e.target.value)}
										onBlur={() => {
											window.setTimeout(
												() => setStateSuggestions([]),
												150,
											);
										}}
										onKeyDown={(event) => {
											if (event.key === "Escape")
												setStateSuggestions([]);
										}}
										className='w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 placeholder:text-slate-400 focus:border-primary focus:outline-none'
									/>
									{stateSuggestions.length > 0 ? (
										<ul className='mt-1 max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-sm'>
											{stateSuggestions.map((suggestion) => (
												<li
													key={`state-${suggestion.value}-${suggestion.label}`}
													className='border-b border-slate-100 last:border-b-0'
												>
													<button
														type='button'
														className='w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50'
														onMouseDown={(event) => {
															event.preventDefault();
															selectStateSuggestion(
																suggestion,
															);
														}}
													>
														{suggestion.label}
													</button>
												</li>
											))}
										</ul>
									) : null}
									{stateSuggestStatus === "loading" ? (
										<p className='mt-1 text-[11px] text-slate-500'>
											Buscando estado...
										</p>
									) : null}
									{addressErrors.name_state ? (
										<p className='mt-1 text-[11px] text-red-500'>
											{addressErrors.name_state}
										</p>
									) : null}
								</div>
								<div>
									<input
										type='text'
										placeholder='Ciudad (ej: Caracas)'
										value={addressForm.name_city}
										onChange={(e) => handleCityInput(e.target.value)}
										onBlur={() => {
											window.setTimeout(
												() => setCitySuggestions([]),
												150,
											);
										}}
										onKeyDown={(event) => {
											if (event.key === "Escape")
												setCitySuggestions([]);
										}}
										className='w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 placeholder:text-slate-400 focus:border-primary focus:outline-none'
									/>
									{citySuggestions.length > 0 ? (
										<ul className='mt-1 max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-sm'>
											{citySuggestions.map((suggestion) => (
												<li
													key={`city-${suggestion.value}-${suggestion.label}`}
													className='border-b border-slate-100 last:border-b-0'
												>
													<button
														type='button'
														className='w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50'
														onMouseDown={(event) => {
															event.preventDefault();
															selectCitySuggestion(
																suggestion,
															);
														}}
													>
														{suggestion.label}
													</button>
												</li>
											))}
										</ul>
									) : null}
									{citySuggestStatus === "loading" ? (
										<p className='mt-1 text-[11px] text-slate-500'>
											Buscando ciudad...
										</p>
									) : null}
									{addressErrors.name_city ? (
										<p className='mt-1 text-[11px] text-red-500'>
											{addressErrors.name_city}
										</p>
									) : null}
								</div>
								<input
									type='text'
									placeholder='Nombre (ej: Mi Casa, Oficina)'
									value={addressForm.name_address}
									onChange={(e) =>
										setAddressForm((f) => ({
											...f,
											name_address: e.target.value,
										}))
									}
									className='w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 placeholder:text-slate-400 focus:border-primary focus:outline-none'
								/>
								{addressErrors.name_address ? (
									<p className='mt-1 text-[11px] text-red-500'>
										{addressErrors.name_address}
									</p>
								) : null}
								<input
									type='text'
									placeholder='Calle / avenida'
									value={addressForm.street_address}
									onChange={(e) =>
										setAddressForm((f) => ({
											...f,
											street_address: e.target.value,
										}))
									}
									className='w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 placeholder:text-slate-400 focus:border-primary focus:outline-none'
								/>
								{addressErrors.street_address ? (
									<p className='mt-1 text-[11px] text-red-500'>
										{addressErrors.street_address}
									</p>
								) : null}
								<input
									type='text'
									placeholder='Referencia'
									value={addressForm.reference_address}
									onChange={(e) =>
										setAddressForm((f) => ({
											...f,
											reference_address: e.target.value,
										}))
									}
									className='w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 placeholder:text-slate-400 focus:border-primary focus:outline-none'
								/>
								{addressErrors.reference_address ? (
									<p className='mt-1 text-[11px] text-red-500'>
										{addressErrors.reference_address}
									</p>
								) : null}
								<div className='flex items-center justify-end gap-2 pt-1'>
									<button
										type='button'
										onClick={() => {
											stateSuggestAbort?.abort();
											citySuggestAbort?.abort();
											if (stateSuggestDebounce !== null) {
												window.clearTimeout(stateSuggestDebounce);
											}
											if (citySuggestDebounce !== null) {
												window.clearTimeout(citySuggestDebounce);
											}
											setShowAddressForm(false);
											setAddressForm({
												name_state: "",
												name_city: "",
												name_address: "",
												street_address: "",
												reference_address: "",
											});
											setAddressErrors({});
											setStateSuggestions([]);
											setCitySuggestions([]);
											setStateSuggestStatus("idle");
											setCitySuggestStatus("idle");
											setStateSuggestAbort(null);
											setCitySuggestAbort(null);
											setStateSuggestDebounce(null);
											setCitySuggestDebounce(null);
										}}
										className='rounded-lg px-3 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-200 cursor-pointer'
									>
										Cancelar
									</button>
									<button
										type='button'
										onClick={handleSaveAddress}
										disabled={
											savingAddress ||
											!addressForm.name_state.trim() ||
											!addressForm.name_city.trim() ||
											!addressForm.name_address.trim() ||
											!addressForm.street_address.trim() ||
											!addressForm.reference_address.trim()
										}
										className='flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer'
									>
										{savingAddress && (
											<Loader2 className='h-3 w-3 animate-spin' />
										)}
										Guardar dirección
									</button>
								</div>
							</div>
						)}
					</div>

					{requireServiceContext && serviceContext ? (
						<div className='border-t border-slate-200 px-6 py-4'>
							<h3 className='mb-2 text-sm font-semibold text-slate-900'>
								Datos de la solicitud
							</h3>
							<div className='grid gap-2 sm:grid-cols-2'>
								<label className='text-xs text-slate-600'>
									Urgencia
									<select
										value={serviceContext.urgency}
										onChange={(e) =>
											onServiceContextChange?.({
												urgency: e.target.value as
													| "urgente"
													| "normal"
													| "",
											})
										}
										className='mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-primary'
									>
										<option value=''>Selecciona</option>
										<option value='normal'>Normal</option>
										<option value='urgente'>Urgente</option>
									</select>
								</label>

								<label className='text-xs text-slate-600'>
									Fecha
									<input
										type='date'
										value={serviceContext.date}
										min={todayIso}
										max={maxDateIso}
										onChange={(e) => {
											const nextDate = e.target.value;
											if (!isValidAvailabilityDate(nextDate)) {
												onServiceContextChange?.({ date: "" });
												return;
											}

											onServiceContextChange?.({
												date: nextDate,
											});
										}}
										className='mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-primary'
									/>
								</label>

								<label className='text-xs text-slate-600'>
									Hora
									<div className='mt-1 flex gap-2'>
										<input
											type='text'
											inputMode='numeric'
											placeholder='hh:mm'
											maxLength={5}
											value={serviceContext.time}
											onChange={(e) => {
												const formatted = formatTimeByRegex(
													e.target.value,
												);
												onServiceContextChange?.({
													time: formatted,
												});
											}}
											onBlur={() => {
												if (!serviceContext.time.trim()) return;
												onServiceContextChange?.({
													time: normalizeTime12Input(
														serviceContext.time,
													),
												});
											}}
											className='w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-primary'
										/>
										<select
											value={serviceContext.period}
											onChange={(e) =>
												onServiceContextChange?.({
													period: e.target.value as "AM" | "PM",
												})
											}
											className='rounded-lg border border-slate-300 px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-primary'
										>
											<option value='AM'>AM</option>
											<option value='PM'>PM</option>
										</select>
									</div>
									{hasInvalidServiceContextTime ? (
										<p className='mt-1 text-[11px] text-amber-600'>
											Usa formato normal de 12 horas (ej: 9:30) y
											selecciona AM/PM.
										</p>
									) : null}
								</label>

								<label className='text-xs text-slate-600'>
									Categoría
									<input
										type='text'
										value={serviceContext.category}
										readOnly
										placeholder='Se extrae de los servicios seleccionados'
										className='mt-1 w-full rounded-lg border border-slate-300 bg-slate-100 px-2 py-1.5 text-xs text-slate-700 outline-none'
									/>
								</label>

								<label className='text-xs text-slate-600'>
									Subcategorías
									<div className='mt-1 min-h-8 w-full rounded-lg border border-slate-300 bg-slate-100 px-2 py-1.5 text-slate-700'>
										{serviceContext.subcategory.length > 0 ? (
											<div className='flex flex-wrap gap-1.5'>
												{serviceContext.subcategory.map(
													(subcategory) => (
														<span
															key={subcategory}
															className='inline-flex max-w-full rounded-full  px-2 py-0.5 text-[11px] font-medium'
															title={subcategory}
														>
															<span className='truncate'>
																{subcategory}
															</span>
														</span>
													),
												)}
											</div>
										) : (
											<span className='text-[11px] text-slate-500'>
												Se extraen de los servicios seleccionados
											</span>
										)}
									</div>
								</label>

								<label className='text-xs text-slate-600'>
									Nombre de ubicación
									<input
										type='text'
										value={serviceContext.addressName}
										readOnly
										maxLength={15}
										placeholder='Se extrae de la dirección seleccionada'
										className='mt-1 w-full rounded-lg border border-slate-300 bg-slate-100 px-2 py-1.5 text-xs text-slate-700 outline-none'
									/>
									{serviceContext.addressName &&
										serviceContext.addressName.length >= 15 && (
											<span className='text-[11px] text-amber-600'>
												Máximo 15 caracteres
											</span>
										)}
								</label>
							</div>
						</div>
					) : null}

					<div className='border-t border-slate-200 px-6 py-4'>
						<label className='block text-xs text-slate-600'>
							Descripcion de la solicitud
							<textarea
								value={requestDescription}
								onChange={(e) => setRequestDescription(e.target.value)}
								placeholder='Describe brevemente lo que necesitas (opcional)'
								rows={3}
								className='mt-1 w-full resize-none rounded-lg border border-slate-300 px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-primary'
							/>
						</label>
						<p className='mt-1 text-[11px] text-slate-500'>
							Si lo dejas vacio, se enviara automaticamente: "
							{autoRequestDescription}".
						</p>
					</div>
				</div>

				<footer className='border-t border-slate-200 px-6 py-4'>
					<div className='flex items-center justify-between text-sm text-slate-600'>
						<span>Total</span>
						<span className='font-semibold text-slate-900'>
							{formatPrice(total)}
						</span>
					</div>
					<button
						type='button'
						onClick={() =>
							onSubmit?.({
								items,
								selectedAddressId,
								requestDescription:
									requestDescription.trim() || autoRequestDescription,
							})
						}
						disabled={!selectedAddressId || hasInvalidServiceContextTime}
						className='mt-4 w-full rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-hover cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
					>
						{submitLabel}
					</button>
					{!selectedAddressId && (
						<p className='mt-1.5 text-center text-xs text-amber-600'>
							Debes seleccionar o agregar una dirección para continuar
						</p>
					)}
					{selectedAddressId && hasInvalidServiceContextTime && (
						<p className='mt-1.5 text-center text-xs text-amber-600'>
							La hora debe estar en formato normal de 12 horas (ej: 9:30).
						</p>
					)}
				</footer>
			</div>
		</div>
	);
}
